/**
 * FilterBar - Reusable filter dropdown component
 * Used across Dashboard, Work Order Map, Work Orders list, etc.
 */
import React from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * FilterSelect - A single filter dropdown
 */
export const FilterSelect = ({
  label,
  value,
  onChange,
  options,
  allLabel = 'All',
  className = '',
  testId,
}) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">
          {label}:
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm border border-slate-200 dark:border-slate-600 rounded-md px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        data-testid={testId}
      >
        <option value="all">{allLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
            {option.count !== undefined && ` (${option.count})`}
          </option>
        ))}
      </select>
    </div>
  );
};

/**
 * FilterBar - A container for multiple filter dropdowns
 * 
 * @param {array} filters - Array of filter configurations
 * @param {function} onClear - Handler to clear all filters
 * @param {boolean} showClear - Show clear button when filters are active
 * @param {string} className - Additional classes
 */
export const FilterBar = ({
  filters = [],
  onClear,
  showClear = true,
  className = '',
  testId,
}) => {
  const hasActiveFilters = filters.some(f => f.value && f.value !== 'all');
  
  return (
    <div 
      className={`flex flex-wrap items-center gap-3 ${className}`}
      data-testid={testId}
    >
      <Filter size={16} className="text-slate-400" />
      
      {filters.map((filter, index) => (
        <FilterSelect
          key={filter.id || index}
          label={filter.label}
          value={filter.value}
          onChange={filter.onChange}
          options={filter.options}
          allLabel={filter.allLabel || `All ${filter.label || ''}`}
          testId={filter.testId}
        />
      ))}
      
      {showClear && hasActiveFilters && onClear && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <X size={14} className="mr-1" />
          Clear
        </Button>
      )}
    </div>
  );
};

/**
 * ClientFacilityFilter - Common filter combo for Client and Facility
 * Used across many pages
 */
export const ClientFacilityFilter = ({
  clients = [],
  facilities = [],
  selectedClient,
  selectedFacility,
  onClientChange,
  onFacilityChange,
  onClear,
  showFacility = true,
  className = '',
}) => {
  // Filter facilities based on selected client
  const filteredFacilities = selectedClient && selectedClient !== 'all'
    ? facilities.filter(f => f.client_id === selectedClient)
    : facilities;
  
  const filters = [
    {
      id: 'client',
      label: 'Client',
      value: selectedClient,
      onChange: (value) => {
        onClientChange(value);
        // Reset facility when client changes
        if (onFacilityChange) {
          onFacilityChange('all');
        }
      },
      options: clients.map(c => ({ value: c.id, label: c.name })),
      allLabel: 'All Clients',
      testId: 'client-filter',
    },
  ];
  
  if (showFacility) {
    filters.push({
      id: 'facility',
      label: 'Facility',
      value: selectedFacility,
      onChange: onFacilityChange,
      options: filteredFacilities.map(f => ({ value: f.id, label: f.name })),
      allLabel: 'All Facilities',
      testId: 'facility-filter',
    });
  }
  
  return (
    <FilterBar
      filters={filters}
      onClear={onClear}
      className={className}
      testId="client-facility-filter"
    />
  );
};

/**
 * StatusFilter - Common status filter dropdown
 */
export const StatusFilter = ({
  value,
  onChange,
  statuses = [],
  label = 'Status',
  className = '',
}) => {
  const defaultStatuses = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];
  
  return (
    <FilterSelect
      label={label}
      value={value}
      onChange={onChange}
      options={statuses.length > 0 ? statuses : defaultStatuses}
      allLabel="All Statuses"
      className={className}
      testId="status-filter"
    />
  );
};

/**
 * PriorityFilter - Common priority filter dropdown
 */
export const PriorityFilter = ({
  value,
  onChange,
  priorities = [],
  label = 'Priority',
  className = '',
}) => {
  const defaultPriorities = [
    { value: 'emergency', label: 'Emergency' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];
  
  return (
    <FilterSelect
      label={label}
      value={value}
      onChange={onChange}
      options={priorities.length > 0 ? priorities : defaultPriorities}
      allLabel="All Priorities"
      className={className}
      testId="priority-filter"
    />
  );
};

export default FilterBar;
